/**
 * RingingUI - Incoming call screen
 * Clean, native-style phone call UI for all platforms
 */

"use client";

import React, { useState, useRef } from "react";
import {
  Phone,
  PhoneOff,
  Video,
  Globe,
  User,
  Users,
  Clock,
  MessageSquare,
  ChevronLeft,
  Send,
} from "lucide-react";
import { isCapacitor } from "@/lib/platformUtils";
import { sipRingStrategyUiLabel } from "@/lib/sipRingStrategyLabels";
import type { RingingUIProps, SipCallData } from "../types";
import { useTheme } from "@/contexts/ThemeContext";
import { BrandedSpinnerApp } from "../../../../remotion/components/BrandedSpinnerApp";

// Detect test/echo calls by caller name patterns
const isTestCall = (name: string) => {
  const n = name.toLowerCase();
  return (
    n.includes("test call") ||
    n.includes("echo test") ||
    n.includes("audio test") ||
    n.includes("extension ready") ||
    n.includes("courtesy") ||
    n.includes("voicemail test") ||
    n.includes("hold music") ||
    n.includes("ivr test") ||
    n.includes("transfer test") ||
    n.includes("system test") ||
    n.includes("full system") ||
    n.includes("pabx") ||
    n.includes("welcome call") ||
    n === "echo" ||
    n === "test"
  );
};

const REMIND_OPTIONS = [
  { label: "1 min", minutes: 1 },
  { label: "5 min", minutes: 5 },
  { label: "15 min", minutes: 15 },
  { label: "1 hour", minutes: 60 },
];

const QUICK_MESSAGES = [
  "I'll call you back shortly",
  "I'm in a meeting",
  "Can't talk right now",
  "Let me call you back later",
];

/** Subtitle under caller name: queue vs ring group vs direct extension routing. */
function sipInboundRouteSubtitle(
  sip: SipCallData | null | undefined,
): string | null {
  if (!sip) return null;

  if (sip.queue_id || sip.queue_name) {
    let line: string;
    if (sip.queue_name) {
      line = `Queue: ${sip.queue_name}`;
    } else if (sip.queue_id) {
      line = `Queue (${sip.queue_id})`;
    } else {
      line = "Queue";
    }
    if (sip.queue_position != null && sip.queue_position > 0) {
      line = `${line} · position ${sip.queue_position}`;
    }
    return line;
  }

  if (sip.ring_group_id || sip.ring_group_name) {
    let line = sip.ring_group_name
      ? `Ring group: ${sip.ring_group_name}`
      : "Ring group call";
    const strategyLabel = sipRingStrategyUiLabel(sip.ring_strategy);
    if (strategyLabel) {
      line = `${line} · ${strategyLabel}`;
    }
    if (sip.ring_timeout != null && sip.ring_timeout > 0) {
      line = `${line} · up to ${sip.ring_timeout}s`;
    }
    return line;
  }

  const to =
    sip.extension_display_name || sip.target_extension || sip.extension_number;
  if (to) {
    return `To: ${to}`;
  }

  return null;
}

function sipInboundIdentitySubtitle(
  sip: SipCallData | null | undefined,
): string | null {
  if (!sip) return null;

  const parts = [
    sip.trunk_name ? `Trunk: ${sip.trunk_name}` : null,
    sip.did_number ? `DID: ${sip.did_number}` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : null;
}

export function RingingUI({
  callType,
  selectedUser,
  screenPopData,
  sipCallData,
  isGroupCall,
  groupCallInfo,
  onAnswer,
  onReject,
  onRemindMe,
  onQuickMessage,
  onHapticFeedback,
  isDark,
  primaryColor,
}: RingingUIProps) {
  const { clientBranding } = useTheme();

  // Sub-panel state for Remind Me / Quick Message
  const [activePanel, setActivePanel] = useState<"none" | "remind" | "message">(
    "none",
  );
  const [customMessage, setCustomMessage] = useState("");

  // Determine call type for styling
  const isVideoCall = callType === "video";
  const isSipRinging =
    sipCallData !== null || selectedUser?.role === "External";

  const callTypeLabel = isVideoCall
    ? "Video Call"
    : isSipRinging
      ? "TotalAccess PBX"
      : "Voice Call";

  // Get caller + trunk context
  const callerNumberDisplay =
    screenPopData?.phone_searched ||
    sipCallData?.caller_number ||
    (selectedUser?.role === 'External' ? selectedUser?.last_name : undefined);
  const sipIdentitySubtitle = sipInboundIdentitySubtitle(sipCallData);
  const routedSubtitle = sipInboundRouteSubtitle(sipCallData);

  // Get caller display name
  const callerName = isGroupCall
    ? groupCallInfo?.threadName
    : screenPopData?.contact?.company_name
      ? screenPopData.contact.company_name
      : screenPopData?.contact?.contact_person
        ? screenPopData.contact.contact_person
        : sipCallData?.caller_name
          ? sipCallData.caller_name
        : selectedUser
          ? `${selectedUser?.first_name || ""} ${selectedUser?.last_name || ""}`.trim() ||
            "Unknown"
          : "Unknown Caller";

  // Get avatar URL
  const avatarUrl =
    screenPopData?.contact?.logo_url ||
    screenPopData?.contact?.photo_url ||
    selectedUser?.avatar_url;

  // Detect test calls
  const testCall = isTestCall(callerName || "");

  // Accent color based on call type
  const accentColor = isVideoCall
    ? "blue"
    : isSipRinging
      ? "purple"
      : "emerald";

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col overflow-hidden bg-[#1a1a2e]">
      {/* Subtle top gradient for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#16213e] via-[#1a1a2e] to-[#0f0f23]" />

      {/* Content container */}
      <div className="relative flex-1 flex flex-col">
        {/* Safe area top spacer */}
        <div className="h-[max(env(safe-area-inset-top),var(--safe-area-top,0px))] flex-shrink-0" />

        {/* Top section - Call type label */}
        <div className="flex items-center justify-center pt-6 pb-2">
          <span
            className={`text-sm font-medium tracking-widest uppercase ${
              isVideoCall
                ? "text-blue-400"
                : isSipRinging
                  ? "text-purple-400"
                  : "text-emerald-400"
            }`}
          >
            {callTypeLabel}
          </span>
        </div>

        {/* Main content - vertically centered */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 -mt-8">
          {/* Avatar / Branded Spinner for test calls */}
          <div className="relative mb-6">
            {testCall ? (
              <BrandedSpinnerApp
                logoUrl={clientBranding?.logo_url}
                primaryColor={primaryColor}
                size={112}
                opacity={0.95}
              />
            ) : (
              <>
                {/* Subtle pulse ring */}
                <div
                  className={`absolute -inset-3 rounded-full opacity-20 animate-pulse ${
                    isVideoCall
                      ? "bg-blue-500"
                      : isSipRinging
                        ? "bg-purple-500"
                        : "bg-emerald-500"
                  }`}
                  style={{ animationDuration: "2s" }}
                />

                <div
                  className={`relative w-28 h-28 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden ring-2 ${
                    isVideoCall
                      ? "ring-blue-400/40"
                      : isSipRinging
                        ? "ring-purple-400/40"
                        : "ring-emerald-400/40"
                  }`}
                >
                  {isGroupCall ? (
                    <Users className="w-12 h-12 text-slate-400" />
                  ) : avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : isSipRinging ? (
                    <Globe className="w-12 h-12 text-slate-400" />
                  ) : (
                    <User className="w-12 h-12 text-slate-400" />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Caller Info */}
          <div className="text-center max-w-sm">
            {/* Caller Name */}
            <h1 className="text-white text-3xl font-bold mb-1.5 leading-tight">
              {callerName}
            </h1>

            {/* Phone Number for SIP calls */}
            {(isSipRinging || callerNumberDisplay) && callerNumberDisplay && (
                <p className="text-slate-400 text-lg font-mono tracking-wider mb-0.5">
                  {callerNumberDisplay}
                </p>
              )}

            {/* Contact Person subtitle */}
            {screenPopData?.contact?.contact_person &&
              screenPopData?.contact?.company_name && (
                <p className="text-slate-400 text-sm mt-2">
                  {screenPopData.contact.contact_person}
                  {screenPopData.contact.position && (
                    <span className="text-slate-600 ml-1.5">
                      • {screenPopData.contact.position}
                    </span>
                  )}
                </p>
              )}

            {isSipRinging && (sipIdentitySubtitle || routedSubtitle) && (
              <div className="mt-4 rounded-2xl border border-purple-400/20 bg-purple-500/10 px-4 py-3 text-left shadow-lg shadow-purple-900/10">
                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-purple-300">
                  TotalAccess PBX
                </div>
                {sipIdentitySubtitle ? (
                  <p className="mt-2 text-sm font-medium text-slate-200">
                    {sipIdentitySubtitle}
                  </p>
                ) : null}
                {routedSubtitle ? (
                  <p className="mt-1 text-sm text-slate-400">{routedSubtitle}</p>
                ) : null}
              </div>
            )}

            {/* "Incoming Call" animated indicator */}
            <div className="flex items-center justify-center gap-1.5 mt-4">
              <span
                className={`inline-block w-1 h-1 rounded-full animate-bounce ${
                  isVideoCall
                    ? "bg-blue-400"
                    : isSipRinging
                      ? "bg-purple-400"
                      : "bg-emerald-400"
                }`}
                style={{ animationDelay: "0ms" }}
              />
              <span
                className={`inline-block w-1 h-1 rounded-full animate-bounce ${
                  isVideoCall
                    ? "bg-blue-400"
                    : isSipRinging
                      ? "bg-purple-400"
                      : "bg-emerald-400"
                }`}
                style={{ animationDelay: "150ms" }}
              />
              <span
                className={`inline-block w-1 h-1 rounded-full animate-bounce ${
                  isVideoCall
                    ? "bg-blue-400"
                    : isSipRinging
                      ? "bg-purple-400"
                      : "bg-emerald-400"
                }`}
                style={{ animationDelay: "300ms" }}
              />
            </div>

            {/* Group call info */}
            {isGroupCall && groupCallInfo?.callerName && (
              <p className="text-slate-500 text-sm mt-3">
                Started by {groupCallInfo.callerName}
              </p>
            )}
            {isGroupCall && groupCallInfo?.participantCount && (
              <div className="flex items-center justify-center gap-1.5 text-slate-500 text-sm mt-1">
                <Users className="w-3.5 h-3.5" />
                <span>{groupCallInfo.participantCount} participants</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom action buttons */}
        <div className="flex-shrink-0 px-6 pb-4">
          {/* Secondary actions - Remind Me / Message */}
          <div className="flex items-center justify-center gap-10 mb-8">
            <button
              onClick={() => {
                onHapticFeedback("medium");
                if (onRemindMe) setActivePanel("remind");
              }}
              className="flex flex-col items-center group"
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center transition-all duration-200 group-active:scale-90">
                <Clock className="w-5 h-5 text-white/70" />
              </div>
              <span className="text-white/50 text-[11px] mt-1.5 font-medium">
                Remind Me
              </span>
            </button>

            <button
              onClick={() => {
                if (isSipRinging) return;
                onHapticFeedback("medium");
                if (onQuickMessage) setActivePanel("message");
              }}
              className={`flex flex-col items-center group ${isSipRinging ? "opacity-30 pointer-events-none" : ""}`}
              disabled={isSipRinging}
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center transition-all duration-200 group-active:scale-90">
                <MessageSquare className="w-5 h-5 text-white/70" />
              </div>
              <span className="text-white/50 text-[11px] mt-1.5 font-medium">
                Message
              </span>
            </button>
          </div>

          {/* Main action buttons - Decline and Accept side by side */}
          <div className="flex items-center justify-center gap-16 mb-2">
            {/* Decline */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => {
                  onHapticFeedback("heavy");
                  onReject();
                }}
                className="w-[72px] h-[72px] rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30 transition-all duration-200 active:scale-90 active:brightness-90"
              >
                <PhoneOff className="w-8 h-8 text-white" />
              </button>
              <span className="text-red-400/80 text-xs mt-2 font-medium">
                Decline
              </span>
            </div>

            {/* Accept */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => {
                  onHapticFeedback("heavy");
                  onAnswer();
                }}
                className={`w-[72px] h-[72px] rounded-full flex items-center justify-center shadow-lg transition-all duration-200 active:scale-90 active:brightness-90 ${
                  isVideoCall
                    ? "bg-blue-500 shadow-blue-500/30"
                    : "bg-green-500 shadow-green-500/30"
                }`}
              >
                {isVideoCall ? (
                  <Video className="w-8 h-8 text-white" />
                ) : (
                  <Phone className="w-8 h-8 text-white" />
                )}
              </button>
              <span
                className={`text-xs mt-2 font-medium ${
                  isVideoCall ? "text-blue-400/80" : "text-green-400/80"
                }`}
              >
                Accept
              </span>
            </div>
          </div>
        </div>

        {/* Safe area bottom spacer */}
        <div className="h-[max(env(safe-area-inset-bottom),var(--safe-area-bottom,0px))] flex-shrink-0" />

        {/* Remind Me Panel */}
        {activePanel === "remind" && (
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md z-10 flex flex-col justify-end">
            <div className="bg-[#1e1e3a] rounded-t-3xl border-t border-white/10 p-6 pb-[max(env(safe-area-inset-bottom),var(--safe-area-bottom,0px))]">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setActivePanel("none")}
                  className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">Back</span>
                </button>
                <h3 className="text-white font-semibold text-lg">Remind Me</h3>
                <div className="w-16" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {REMIND_OPTIONS.map((opt) => (
                  <button
                    key={opt.minutes}
                    onClick={() => {
                      onHapticFeedback("medium");
                      onRemindMe?.(opt.minutes);
                      setActivePanel("none");
                      onReject();
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all active:scale-95"
                  >
                    <Clock className="w-4 h-4 text-blue-400" />
                    <span className="text-white font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quick Message Panel */}
        {activePanel === "message" && (
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md z-10 flex flex-col justify-end">
            <div className="bg-[#1e1e3a] rounded-t-3xl border-t border-white/10 p-6 pb-[max(env(safe-area-inset-bottom),var(--safe-area-bottom,0px))]">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setActivePanel("none")}
                  className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">Back</span>
                </button>
                <h3 className="text-white font-semibold text-lg">
                  Quick Message
                </h3>
                <div className="w-16" />
              </div>

              <div className="space-y-2 mb-4">
                {QUICK_MESSAGES.map((msg, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      onHapticFeedback("medium");
                      onQuickMessage?.(msg);
                      setActivePanel("none");
                      onReject();
                    }}
                    className="w-full text-left px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all active:scale-[0.98]"
                  >
                    <span className="text-white text-sm">{msg}</span>
                  </button>
                ))}
              </div>

              {/* Custom message input */}
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                <input
                  type="text"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Custom message..."
                  className="flex-1 bg-transparent text-white text-sm outline-none placeholder-slate-500"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && customMessage.trim()) {
                      onHapticFeedback("medium");
                      onQuickMessage?.(customMessage.trim());
                      setCustomMessage("");
                      setActivePanel("none");
                      onReject();
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (customMessage.trim()) {
                      onHapticFeedback("medium");
                      onQuickMessage?.(customMessage.trim());
                      setCustomMessage("");
                      setActivePanel("none");
                      onReject();
                    }
                  }}
                  disabled={!customMessage.trim()}
                  className={`p-2 rounded-lg transition-colors ${
                    customMessage.trim()
                      ? "bg-blue-500 hover:bg-blue-400"
                      : "bg-white/10 opacity-50"
                  }`}
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RingingUI;
